import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import './MachinePartsReport.css';

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const MachinePartsReport = () => {
  const currentDate = new Date();
  const [month, setMonth] = useState(MONTHS[currentDate.getMonth()]);
  const [year, setYear] = useState(currentDate.getFullYear().toString());
  const [data, setData] = useState({});
  const [countsData, setCountsData] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [machineFilter, setMachineFilter] = useState('');
  const [partFilter, setPartFilter] = useState('');

  const [activeTab, setActiveTab] = useState(0);

  // Generate years list (e.g., from 2020 to current + 5)
  const currentYear = currentDate.getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => (currentYear - 5 + i).toString());

  const fetchData = async () => {
    setLoading(true);
    try {
      const payload = { month: month, year: year, index: "ifec" };
      const config = {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      };

      const [partsRes, countsRes] = await Promise.all([
        axios.post('https://apir4prod.bsynapse.com/ifeccustomguard/partsworkedmachinewise', payload, config),
        axios.post('https://apir4prod.bsynapse.com/ifeccustomguard/numberofpartsworkedmachinewise', payload, config)
      ]);

      setData(partsRes.data || {});
      setCountsData(countsRes.data || {});
    } catch (error) {
      console.error("Error fetching machine parts data:", error);
      setData({});
      setCountsData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [month, year]);

  // Determine the number of days in the selected month
  const monthIndex = MONTHS.indexOf(month);
  const daysInMonth = new Date(parseInt(year), monthIndex + 1, 0).getDate();
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Filter data
  const allMachinesSet = new Set(Object.keys(data));
  Object.values(countsData).forEach(dateObj => {
    if (dateObj && dateObj.machines) {
      Object.keys(dateObj.machines).forEach(machine => allMachinesSet.add(machine));
    }
  });
  const allMachines = Array.from(allMachinesSet).sort();

  const filteredMachines = allMachines.filter(machine => 
    machine.toLowerCase().includes(machineFilter.toLowerCase())
  );

  const getFormattedDate = (d) => {
    const mm = String(monthIndex + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', overflow: 'auto', backgroundColor: '#f8fafc' }}>
      <Paper 
        sx={{ 
          p: 1.5, 
          px: 3,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderRadius: 0,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', 
          gap: 2, 
          alignItems: 'center',
          flexWrap: 'wrap',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)'
        }} 
        elevation={2}
      >
        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel>Month</InputLabel>
          <Select
            value={month}
            label="Month"
            onChange={(e) => setMonth(e.target.value)}
          >
            {MONTHS.map(m => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 100 }} size="small">
          <InputLabel>Year</InputLabel>
          <Select
            value={year}
            label="Year"
            onChange={(e) => setYear(e.target.value)}
          >
            {years.map(y => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField 
          label="Filter Machine" 
          variant="outlined" 
          size="small"
          value={machineFilter}
          onChange={(e) => setMachineFilter(e.target.value)}
        />

        <TextField 
          label="Filter Part Number" 
          variant="outlined" 
          size="small"
          value={partFilter}
          onChange={(e) => setPartFilter(e.target.value)}
        />

        <Box sx={{ flexGrow: 1 }} />
        
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)} 
          aria-label="machine parts tabs"
          sx={{ 
            minHeight: '40px',
            '& .MuiTab-root': { minHeight: '40px', py: 1, textTransform: 'none', fontWeight: 600 }
          }}
        >
          <Tab label="Parts worked Machine wise" />
          <Tab label="Number of parts worked Machine wise" />
        </Tabs>
      </Paper>

      <Box sx={{ p: 3 }}>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 0 && (
          <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Parts worked Machine wise
            </Typography>
          <TableContainer component={Paper} className="machine-parts-table-container">
            <Table stickyHeader size="small" aria-label="parts worked machine wise table" sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #e0e0e0', minWidth: 120, zIndex: 3, left: 0, position: 'sticky' }}>
                    Machine
                  </TableCell>
                  <TableCell colSpan={daysInMonth} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                    Month ({month})
                  </TableCell>
                </TableRow>
                <TableRow>
                  {daysArray.map(day => (
                    <TableCell key={day} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 80 }}>
                      {day}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMachines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={daysInMonth + 1} align="center">
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMachines.map(machine => {
                    const machineData = data[machine] || {};

                    return (
                      <TableRow key={machine} hover>
                        <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0', position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}>
                          {machine}
                        </TableCell>
                        
                        {daysArray.map(day => {
                          const dateKey = getFormattedDate(day);
                          let parts = machineData[dateKey] || [];
                          
                          if (partFilter) {
                            parts = parts.filter(p => 
                              p.toLowerCase().includes(partFilter.toLowerCase())
                            );
                          }

                          return (
                            <TableCell key={day} align="center" sx={{ p: 0.5, borderRight: '1px solid #f0f0f0' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {parts.length > 0 ? parts.map((part, idx) => (
                                  <Box key={idx} className="part-box">
                                    {part}
                                  </Box>
                                )) : null}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Paper>
          )}

          {activeTab === 1 && (
          <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Number of parts worked Machine wise
            </Typography>
            <TableContainer component={Paper} className="machine-parts-table-container">
              <Table stickyHeader size="small" aria-label="number of parts worked machine wise table" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRight: '1px solid #e0e0e0', minWidth: 120, zIndex: 3, left: 0, position: 'sticky' }}>
                      Machine
                    </TableCell>
                    <TableCell colSpan={daysInMonth + 1} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                      Month ({month})
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {daysArray.map(day => (
                      <TableCell key={day} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 80 }}>
                        {day}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f1f5f9', minWidth: 100, borderLeft: '2px solid #e0e0e0', position: 'sticky', right: 0, zIndex: 3 }}>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Total Row */}
                  <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                    <TableCell sx={{ 
                      fontWeight: 'bold', 
                      borderRight: '1px solid #e2e8f0', 
                      position: 'sticky', 
                      left: 0, 
                      backgroundColor: '#f1f5f9', 
                      zIndex: 1,
                      color: '#0f172a'
                    }}>
                      TOTAL
                    </TableCell>
                    {daysArray.map(day => {
                      const dateKey = getFormattedDate(day);
                      const dayData = countsData[dateKey] || {};
                      const dayTotal = dayData.day_unique_parts_count || 0;
                      
                      return (
                        <TableCell key={day} align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #e2e8f0', backgroundColor: '#f1f5f9' }}>
                          {dayTotal > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box className="part-box total-box">
                                {dayTotal}
                              </Box>
                            </Box>
                          ) : '-'}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #e2e8f0', backgroundColor: '#e2e8f0', borderLeft: '2px solid #cbd5e1', position: 'sticky', right: 0, zIndex: 1 }}>
                      <Box className="part-box total-box" style={{ backgroundColor: '#0f172a', color: '#fff' }}>
                        {daysArray.reduce((sum, day) => {
                          const dateKey = getFormattedDate(day);
                          return sum + (countsData[dateKey]?.day_unique_parts_count || 0);
                        }, 0)}
                      </Box>
                    </TableCell>
                  </TableRow>

                  {filteredMachines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={daysInMonth + 2} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMachines.map(machine => {
                      let machineTotal = 0;
                      return (
                        <TableRow key={machine} hover>
                          <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0', position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }}>
                            {machine}
                          </TableCell>
                          
                          {daysArray.map(day => {
                            const dateKey = getFormattedDate(day);
                            const dayData = countsData[dateKey] || {};
                            const machineMap = dayData.machines || {};
                            const partsArray = machineMap[machine] || [];
                            const count = partsArray.length;
                            machineTotal += count;

                            return (
                              <TableCell key={day} align="center" sx={{ p: 0.5, borderRight: '1px solid #f0f0f0' }}>
                                {count > 0 ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box className="part-box">
                                      {count}
                                    </Box>
                                  </Box>
                                ) : null}
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ fontWeight: 'bold', borderLeft: '2px solid #f0f0f0', backgroundColor: '#f8fafc', position: 'sticky', right: 0, zIndex: 1 }}>
                            {machineTotal > 0 ? (
                              <Box className="part-box" style={{ backgroundColor: '#334155', color: '#fff' }}>
                                {machineTotal}
                              </Box>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          )}
        </>
      )}
      </Box>
    </Box>
  );
};

export default MachinePartsReport;
